import { Component, EventEmitter, OnInit, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LiveCallAuthService } from './live-call-auth.service';
import { LiveCallAuthConfig } from './live-call-auth.models';
import {
  AuthFormPanelComponent,
  AuthPanelMode,
} from './components/auth-form-panel/auth-form-panel.component';

/**
 * Auth sub-section of the Agent tab: the catalog of UNIVERSAL API credentials
 * the live-call tools authenticate with — a name, base URL, auth scheme, and a
 * write-only token. Not product-scoped and not environment-scoped: every
 * entry is visible in both Test and Production, so there is no env selector
 * and no default to transfer, unlike the Models sub-tab.
 */
@Component({
  selector: 'app-live-call-auth',
  standalone: true,
  imports: [CommonModule, FormsModule, AuthFormPanelComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './live-call-auth.component.html',
})
export class LiveCallAuthComponent implements OnInit {
  private authService = inject(LiveCallAuthService);

  /** Emits the entry count after each load so the sub-tab shell can badge it. */
  @Output() countChange = new EventEmitter<number>();

  authConfigs = signal<LiveCallAuthConfig[]>([]);
  loading = signal(false);
  errorMsg = signal('');
  includeInactive = signal(true);

  // Panel
  panelOpen = signal(false);
  panelMode = signal<AuthPanelMode>('create');
  selectedAuthConfig = signal<LiveCallAuthConfig | null>(null);

  // Delete
  deleteTarget = signal<LiveCallAuthConfig | null>(null);
  deleting = signal(false);

  ngOnInit(): void {
    this.loadAuthConfigs();
  }

  loadAuthConfigs(): void {
    this.loading.set(true);
    this.errorMsg.set('');
    this.authService.listAuthConfigs(this.includeInactive()).subscribe({
      next: (res) => {
        this.authConfigs.set(res.authConfigs ?? []);
        this.countChange.emit(res.count ?? res.authConfigs?.length ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.authConfigs.set([]);
        this.countChange.emit(0);
        this.errorMsg.set(err?.message ?? 'Failed to load auth entries.');
        this.loading.set(false);
      },
    });
  }

  toggleIncludeInactive(): void {
    this.includeInactive.set(!this.includeInactive());
    this.loadAuthConfigs();
  }

  // ── panel ──────────────────────────────────────────────────────────────────

  openPanel(mode: AuthPanelMode, authConfig: LiveCallAuthConfig | null): void {
    this.panelMode.set(mode);
    this.selectedAuthConfig.set(authConfig);
    this.panelOpen.set(true);
  }

  closePanel(): void {
    this.panelOpen.set(false);
    this.selectedAuthConfig.set(null);
  }

  onSaved(): void {
    this.closePanel();
    this.loadAuthConfigs();
  }

  // ── writes ─────────────────────────────────────────────────────────────────

  toggleActive(entry: LiveCallAuthConfig): void {
    this.errorMsg.set('');
    this.authService.patchAuthConfig(entry.id, { is_active: !entry.isActive }).subscribe({
      next: () => this.loadAuthConfigs(),
      error: (err) => this.errorMsg.set(err?.message ?? 'Could not update active status.'),
    });
  }

  confirmDelete(entry: LiveCallAuthConfig): void {
    this.deleteTarget.set(entry);
  }

  cancelDelete(): void {
    this.deleteTarget.set(null);
  }

  executeDelete(): void {
    const target = this.deleteTarget();
    if (!target) return;
    this.deleting.set(true);
    this.authService.deleteAuthConfig(target.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.cancelDelete();
        this.loadAuthConfigs();
      },
      error: (err) => {
        this.deleting.set(false);
        this.cancelDelete();
        this.errorMsg.set(err?.message ?? 'Delete failed.');
      },
    });
  }

  trackByAuthConfig(_: number, entry: LiveCallAuthConfig): number {
    return entry.id;
  }
}
